import translate from '@translate';
import React, { FC, useEffect, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import ChartContainer from './ChartContainer';
import { styled, Theme, useTheme } from '@mui/material/styles';
import { colors, sizes } from 'src/themes/default';
import OrdersContainer from './OrdersContainer';
import useMediaQuery from '@mui/material/useMediaQuery';
import OrdersInStatuses from './OrdersInStatuses';
import { useDebounce } from 'src/utils/debounce';
import ApplicationStatusMessagesContainer from './ApplicationStatusMessagesContainer';
import CarriersContainer from './CarriersContainer';

const PercentageBlockItem = styled('div')(({ theme }) => ({
    // position: 'absolute',
    padding: sizes.body_PD_N / 2,
    '&>div': {
        width: '100%',
        height: '100%',
        backgroundColor: colors.background_W,
        padding: sizes.body_PD_N,
    },
}));

interface PercentageBlockItemType {
    top: number;
    bottom: number;
    left: number;
    right: number;
    children: ReactNode;
}
interface PercentageBlockPropsType {
    items: PercentageBlockItemType[];
    mobile?: boolean;
}

const PercentageBlock: FC<PercentageBlockPropsType> = (props) => {
    return (
        <div
            style={{
                width: '100%',
                height: '100%',
            }}
        >
            {props.items
                .filter((item) => item.children)
                .map((item, key) => (
                    <PercentageBlockItem
                        sx={
                            props.mobile
                                ? { position: 'relative' }
                                : {
                                      position: 'absolute',
                                      top: `${item.top}%`,
                                      bottom: `${item.bottom}%`,
                                      left: `${item.left}%`,
                                      right: `${item.right}%`,
                                      paddingTop: item.top === 0 ? 0 : undefined,
                                      paddingBottom: item.bottom === 0 ? 0 : undefined,
                                      paddingLeft: item.left === 0 ? 0 : undefined,
                                      paddingRight: item.right === 0 ? 0 : undefined,
                                  }
                        }
                        key={key}
                    >
                        <div>{item.children}</div>
                    </PercentageBlockItem>
                ))}
        </div>
    );
};

const OverviewContainer: FC = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const mobile = useDebounce(useMediaQuery(theme.breakpoints.down('sm')), 500);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('overview.title') }));
    }, []);

    return (
        <>
            <ApplicationStatusMessagesContainer />
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <PercentageBlock
                    mobile={mobile}
                    items={[
                        {
                            top: 0,
                            bottom: 50,
                            left: 0,
                            right: 30,
                            children: (
                                <div style={mobile ? { height: '100%' } : {}}>
                                    <ChartContainer mobile={mobile} />
                                </div>
                            ),
                        },
                        {
                            top: 0,
                            bottom: 55,
                            left: 70,
                            right: 0,
                            children: (
                                <div style={{ maxHeight: '100%', overflowY: 'auto' }}>
                                    <OrdersInStatuses mobile={mobile} />
                                </div>
                            ),
                        },
                        {
                            top: 50,
                            bottom: 0,
                            left: 0,
                            right: 30,
                            children: (
                                <div
                                    style={
                                        mobile
                                            ? { position: 'relative' }
                                            : {
                                                  top: sizes.body_PD_N,
                                                  bottom: sizes.body_PD_N,
                                                  left: sizes.body_PD_N,
                                                  right: sizes.body_PD_N,
                                                  position: 'absolute',
                                              }
                                    }
                                >
                                    <OrdersContainer mobile={mobile} />
                                </div>
                            ),
                        },
                        {
                            top: 45,
                            bottom: 0,
                            left: 70,
                            right: 0,
                            children: (
                                <div style={{ height: '100%' }}>
                                    <CarriersContainer mobile={mobile} />
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        </>
    );
};

export default OverviewContainer;
